﻿import { QueryClient, useInfiniteQuery, useMutation, useQuery } from '@tanstack/react-query';
import axios from 'axios';

import { ALL_DEVICES_QUERY_KEY, DEVICE_API_URL, DEVICE_BY_ID_QUERY_KEY } from '@/constants';
import { apiUrl, getQueryParams } from '@/helpers';
import { CreateEditDevicePayload, ErrorResponse, Options } from '@/types';

export const deviceEndpoints = {
  getAllDevices: (
    pageSize: number,
    cursor: number,
    searchQuery?: string,
    online?: boolean,
    lastSeen?: string,
    enabled?: boolean,
    organizationId?: number
  ) =>
    axios.get(
      `${apiUrl()}${DEVICE_API_URL}${getQueryParams({
        'paging.PageSize': pageSize as number,
        'paging.Cursor': cursor as number,
        'search.Text': searchQuery as string,
        'filter.Online': online as boolean,
        'filter.Enabled': enabled as boolean,
        'filter.Organizations[0].Id': organizationId as number,
        'filter.LastSeen': lastSeen as string
      })}`
    ),
  createDevice: (payload: CreateEditDevicePayload) =>
    axios.post(`${apiUrl()}${DEVICE_API_URL}`, payload),
  editDevice: (payload: CreateEditDevicePayload) =>
    axios.put(`${apiUrl()}${DEVICE_API_URL}`, payload),
  getDevice: (id: number) => axios.get(`${apiUrl()}${DEVICE_API_URL}/${id}`),
  deleteDevice: (id: number) => axios.delete(`${apiUrl()}${DEVICE_API_URL}/${id}`)
};

export const useGetAllDevices = (
  pageSize: number,
  searchQuery?: string,
  online?: boolean,
  lastSeen?: string,
  enabled?: boolean,
  organizationId?: number,
  hasPermission?: boolean
) => {
  return useInfiniteQuery(
    [ALL_DEVICES_QUERY_KEY, { pageSize, searchQuery, online, lastSeen, enabled, organizationId }],
    ({ pageParam }) =>
      deviceEndpoints.getAllDevices(
        pageSize,
        pageParam,
        searchQuery,
        online,
        lastSeen,
        enabled,
        organizationId
      ),
    {
      keepPreviousData: true,
      enabled: hasPermission,
      getNextPageParam: (lastPage) => {
        if (lastPage && lastPage.data?.nextCursor !== undefined) {
          return lastPage.data?.nextCursor;
        }
        return undefined;
      },
      cacheTime: 1000
    }
  );
};

export const useCreateDeviceMutation = (queryClient: QueryClient, options: Options) => {
  return useMutation(deviceEndpoints.createDevice, {
    onSuccess: () => {
      options.onSuccess();
      queryClient.invalidateQueries([ALL_DEVICES_QUERY_KEY]);
    },
    onError: (error) => {
      options.onError(error as ErrorResponse);
    }
  });
};

export const useUpdateDeviceMutation = (queryClient: QueryClient, options: Options) => {
  return useMutation(deviceEndpoints.editDevice, {
    onSuccess: () => {
      options.onSuccess();
      queryClient.invalidateQueries([ALL_DEVICES_QUERY_KEY]);
    },
    onError: (error) => {
      options.onError(error as ErrorResponse);
    }
  });
};

export const useGetDeviceQuery = (id: number, options?: Options) => {
  return useQuery([DEVICE_BY_ID_QUERY_KEY, { id }], () => deviceEndpoints.getDevice(id), {
    enabled: !!id,
    retry: false,
    onError: (error) => {
      options?.onError(error as ErrorResponse);
    }
  });
};

export const useDeleteDeviceMutation = (queryClient: QueryClient, options: Options) => {
  return useMutation(deviceEndpoints.deleteDevice, {
    onSuccess: () => {
      options.onSuccess();
      queryClient.invalidateQueries([ALL_DEVICES_QUERY_KEY]);
    },
    onError: (error) => {
      options.onError(error as ErrorResponse);
    }
  });
};
